import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, MessageCircle, QrCode, Power, RefreshCw, Send, Sparkles, Save, Unplug, RotateCcw,
  CheckCircle2, Clock, AlertTriangle, XCircle, Trash2, ExternalLink, Wand2,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useRealtimeEvents } from "@/hooks/useRealtimeEvents";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtDT = (iso) => { try { return new Date(iso).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" }); } catch { return iso || "—"; } };

const STATE_LABEL = {
  open: ["Conectado", "text-emerald-300 bg-emerald-500/15 border-emerald-400/40"],
  qr: ["Esperando escaneo del QR", "text-amber-300 bg-amber-500/15 border-amber-400/40"],
  connecting: ["Conectando…", "text-sky-300 bg-sky-500/15 border-sky-400/40"],
  starting: ["Arrancando servicio…", "text-sky-300 bg-sky-500/15 border-sky-400/40"],
  closed: ["Desconectado", "text-rose-300 bg-rose-500/15 border-rose-400/40"],
  logged_out: ["Desvinculado", "text-rose-300 bg-rose-500/15 border-rose-400/40"],
  offline: ["Servicio detenido", "text-rose-300 bg-rose-500/15 border-rose-400/40"],
};
const OUTBOX_STYLE = {
  sent: ["Enviado", "text-emerald-300", CheckCircle2],
  queued: ["En cola", "text-sky-300", Clock],
  manual: ["Manual", "text-amber-300", AlertTriangle],
  skipped: ["Omitido", "text-neutral-400", XCircle],
  failed: ["Falló", "text-rose-300", XCircle],
};

/** WhatsAppPanel — link the owner's WhatsApp (QR), toggle automation, edit AI
 *  templates and watch the outbox. */
export const WhatsAppPanel = () => {
  const { authHeader } = useAuth();
  const [st, setSt] = useState(null);
  const [qr, setQr] = useState(null);
  const [outbox, setOutbox] = useState([]);
  const [templates, setTemplates] = useState({});
  const [eventsEnabled, setEventsEnabled] = useState({});
  const [delays, setDelays] = useState({ min: 5, max: 12 });
  const [busy, setBusy] = useState("");
  const [testPhone, setTestPhone] = useState("");
  const [testText, setTestText] = useState("✨ Prueba de CrisAngel Astral: los mensajes automáticos están funcionando.");
  const [openTpl, setOpenTpl] = useState(null);
  const pollRef = useRef(null);

  const load = async (silent = true) => {
    try {
      const r = await axios.get(`${API}/admin/whatsapp/status`, { headers: authHeader() });
      setSt(r.data);
      if (!silent || !Object.keys(templates).length) {
        setTemplates(r.data.settings.templates);
        setEventsEnabled(r.data.settings.events_enabled);
        setDelays({ min: r.data.settings.min_delay, max: r.data.settings.max_delay });
      }
      if (r.data.qr_available) {
        const q = await axios.get(`${API}/admin/whatsapp/qr`, { headers: authHeader() });
        setQr(q.data.qr_data_url);
      } else setQr(null);
    } catch (e) { if (!silent) toast.error(e?.response?.data?.detail || "Error"); }
  };
  const loadOutbox = async () => {
    try { const r = await axios.get(`${API}/admin/whatsapp/outbox?limit=40`, { headers: authHeader() }); setOutbox(r.data); } catch { /* noop */ }
  };
  useEffect(() => {
    load(false); loadOutbox();
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line
  }, []);
  // Poll faster while pairing (QR rotates every ~20s) and slower once linked
  useEffect(() => {
    clearInterval(pollRef.current);
    const ms = st?.connected ? 10000 : 3000;
    pollRef.current = setInterval(() => load(true), ms);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line
  }, [st?.connected]);
  useRealtimeEvents({ admin_refresh: (d) => { if (d?.scope === "whatsapp") loadOutbox(); } });

  const patch = async (body, okMsg) => {
    setBusy("save");
    try {
      const r = await axios.patch(`${API}/admin/whatsapp/settings`, body, { headers: authHeader() });
      setSt((s) => ({ ...s, settings: r.data.settings }));
      toast.success(okMsg || "Guardado");
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar"); }
    finally { setBusy(""); }
  };
  const toggleEnabled = () => patch({ enabled: !st.settings.enabled }, st.settings.enabled ? "Envío automático desactivado" : "Envío automático activado");
  const saveTemplates = () => patch({ templates, events_enabled: eventsEnabled, min_delay: Number(delays.min), max_delay: Number(delays.max) }, "Plantillas y pausas guardadas");
  const generateAI = async () => {
    setBusy("ai");
    try {
      const r = await axios.post(`${API}/admin/whatsapp/templates/generate`, {}, { headers: authHeader() });
      setTemplates(r.data.templates);
      toast.success("Plantillas generadas con IA (ya guardadas)");
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo generar"); }
    finally { setBusy(""); }
  };
  const sendTest = async () => {
    setBusy("test");
    try {
      await axios.post(`${API}/admin/whatsapp/test`, { phone: testPhone, text: testText }, { headers: authHeader() });
      toast.success("Mensaje de prueba enviado");
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo enviar"); }
    finally { setBusy(""); }
  };
  const logout = async () => {
    if (!window.confirm("¿Desvincular tu WhatsApp? Se detendrán los envíos automáticos hasta volver a escanear el QR.")) return;
    setBusy("logout");
    try { await axios.post(`${API}/admin/whatsapp/logout`, {}, { headers: authHeader() }); toast.success("WhatsApp desvinculado"); await load(true); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setBusy(""); }
  };
  const restart = async () => {
    setBusy("restart");
    try { await axios.post(`${API}/admin/whatsapp/restart`, {}, { headers: authHeader() }); toast.success("Servicio reiniciado"); setTimeout(() => load(true), 3000); }
    catch { toast.error("No se pudo reiniciar"); } finally { setBusy(""); }
  };
  const newQr = async () => {
    setBusy("qr");
    setQr(null);
    try { await axios.post(`${API}/admin/whatsapp/reconnect`, {}, { headers: authHeader() }); toast.success("Generando un QR nuevo…"); setTimeout(() => load(true), 2500); }
    catch { toast.error("No se pudo generar el QR"); } finally { setBusy(""); }
  };
  const outboxAction = async (id, action) => {
    try {
      if (action === "delete") await axios.delete(`${API}/admin/whatsapp/outbox/${id}`, { headers: authHeader() });
      else await axios.post(`${API}/admin/whatsapp/outbox/${id}/${action}`, {}, { headers: authHeader() });
      loadOutbox();
    } catch { toast.error("Error"); }
  };

  if (!st) return <div className="text-center py-12"><Loader2 className="w-7 h-7 animate-spin text-[#d4af37] mx-auto" /></div>;
  const [stateLabel, stateCls] = STATE_LABEL[st.state] || STATE_LABEL.offline;
  const enabled = st.settings.enabled;

  return (
    <div className="space-y-4" data-testid="whatsapp-panel">
      {/* Connection */}
      <div className="glass-dark rounded-2xl p-5">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
          <div>
            <h2 className="font-serif text-xl text-white flex items-center gap-2"><MessageCircle className="w-5 h-5 text-emerald-300" /> WhatsApp automático</h2>
            <p className="text-xs text-neutral-400 mt-1 max-w-xl">
              Mensajes automáticos a tus clientes desde <b className="text-white">tu propio número</b> (abonos, liquidaciones, pagos reflejados, citas, promociones, oportunidades y recordatorios).
              Vincula tu WhatsApp como dispositivo: en tu teléfono abre <b className="text-white">WhatsApp → Dispositivos vinculados → Vincular un dispositivo</b> y escanea el QR.
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <span data-testid="wa-state" className={`text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border ${stateCls}`}>{stateLabel}</span>
            {st.me && <span className="text-[10px] text-neutral-400">{st.me.name || ""} · {String(st.me.id || "").split(":")[0].split("@")[0]}</span>}
          </div>
        </div>

        <div className="grid md:grid-cols-[auto_1fr] gap-5 items-start">
          <div className="flex flex-col items-center gap-2">
            {st.connected ? (
              <div className="w-56 h-56 rounded-2xl border border-emerald-400/40 bg-emerald-500/10 flex flex-col items-center justify-center text-center p-4" data-testid="wa-connected-box">
                <CheckCircle2 className="w-12 h-12 text-emerald-300 mb-2" />
                <div className="text-sm text-white font-semibold">Número vinculado</div>
                <div className="text-[11px] text-neutral-400 mt-1">Los mensajes se envían desde tu WhatsApp.</div>
              </div>
            ) : qr ? (
              <div className="relative" data-testid="wa-qr-wrap">
                <img data-testid="wa-qr" src={qr} alt="QR de vinculación de WhatsApp" className="w-56 h-56 rounded-2xl border-4 border-white bg-white" />
                <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 text-[9px] px-2 py-0.5 rounded-full bg-black/80 border border-white/10 text-neutral-300 whitespace-nowrap" data-testid="wa-qr-age">
                  {st.qr_age_sec != null ? `QR de hace ${st.qr_age_sec}s · se renueva solo` : "Se renueva solo"}
                </div>
              </div>
            ) : (
              <div className="w-56 h-56 rounded-2xl border border-white/10 bg-black/40 flex flex-col items-center justify-center text-center p-4" data-testid="wa-waiting-box">
                {st.setup_phase === "error" ? <AlertTriangle className="w-8 h-8 text-rose-300 mb-2" /> : <Loader2 className="w-8 h-8 animate-spin text-[#d4af37] mb-2" />}
                <div className="text-xs text-neutral-300" data-testid="wa-setup-detail">
                  {st.setup_phase === "error" ? "No se pudo iniciar el servicio"
                    : !st.service_up ? (st.setup_detail || "Arrancando el servicio de WhatsApp…")
                    : st.state === "logged_out" ? "Sesión cerrada · generando QR…"
                    : "Generando código QR…"}
                </div>
                {!st.service_up && st.setup_phase !== "error" && <div className="text-[10px] text-neutral-500 mt-1">La primera vez puede tardar hasta 1 minuto.</div>}
                {st.setup_phase === "error" && st.last_error && <div className="text-[10px] text-rose-300 mt-1 break-words max-h-20 overflow-y-auto">{st.last_error}</div>}
                {st.setup_phase === "error" && <button data-testid="wa-retry" disabled={!!busy} onClick={restart} className="mt-2 text-[10px] px-3 py-1 rounded-full bg-[#d4af37] text-black font-bold">Reintentar</button>}
              </div>
            )}
            <div className="flex gap-2 mt-1">
              {!st.connected && <button data-testid="wa-new-qr" disabled={!!busy} onClick={newQr} className="text-[10px] px-3 py-1.5 rounded-full border border-emerald-400/50 text-emerald-200 hover:bg-emerald-500/10 flex items-center gap-1 disabled:opacity-50">{busy === "qr" ? <Loader2 className="w-3 h-3 animate-spin" /> : <QrCode className="w-3 h-3" />} Nuevo QR</button>}
              <button data-testid="wa-restart" disabled={!!busy} onClick={restart} className="text-[10px] px-3 py-1.5 rounded-full border border-white/15 text-neutral-300 hover:text-white flex items-center gap-1 disabled:opacity-50"><RotateCcw className="w-3 h-3" /> Reiniciar</button>
              {st.connected && <button data-testid="wa-logout" disabled={!!busy} onClick={logout} className="text-[10px] px-3 py-1.5 rounded-full border border-rose-400/40 text-rose-200 hover:bg-rose-500/10 flex items-center gap-1 disabled:opacity-50"><Unplug className="w-3 h-3" /> Desvincular</button>}
            </div>
          </div>

          <div className="space-y-3">
            <button
              data-testid="wa-toggle-enabled"
              onClick={toggleEnabled}
              disabled={busy === "save"}
              className={`w-full sm:w-auto px-5 py-3 rounded-2xl border-2 flex items-center gap-3 text-left transition ${enabled ? "border-emerald-400/60 bg-emerald-500/10" : "border-white/10 bg-black/40 hover:border-emerald-400/40"}`}
            >
              <Power className={`w-6 h-6 ${enabled ? "text-emerald-300" : "text-neutral-500"}`} />
              <div>
                <div className="text-sm text-white font-semibold">Envío automático {enabled ? "ACTIVADO" : "desactivado"}</div>
                <div className="text-[11px] text-neutral-400">{enabled ? "Los mensajes en cola se envían uno por uno con pausas humanas." : "Los mensajes se guardan para enviarlos manualmente (wa.me)."}</div>
              </div>
            </button>
            {!st.connected && enabled && <div className="text-[11px] text-amber-300 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> WhatsApp no está conectado: los mensajes esperan en cola hasta que escanees el QR.</div>}

            <div className="grid grid-cols-3 gap-2 text-center">
              {[["queued", "En cola"], ["sent", "Enviados"], ["failed", "Fallidos"]].map(([k, l]) => (
                <div key={k} className="rounded-lg bg-black/40 border border-white/5 p-2" data-testid={`wa-count-${k}`}>
                  <div className={`text-lg font-bold ${k === "sent" ? "text-emerald-300" : k === "failed" ? "text-rose-300" : "text-sky-300"}`}>{st.queue?.[k] || 0}</div>
                  <div className="text-[9px] uppercase tracking-widest text-neutral-500">{l}</div>
                </div>
              ))}
            </div>

            <div className="rounded-lg bg-black/40 border border-white/5 p-3">
              <div className="text-[10px] uppercase tracking-widest text-neutral-400 mb-1.5">Pausa anti-spam entre mensajes (segundos)</div>
              <div className="flex items-center gap-2 text-xs">
                <input data-testid="wa-delay-min" type="number" min={2} max={120} value={delays.min} onChange={(e) => setDelays((d) => ({ ...d, min: e.target.value }))} className="w-20 bg-black/40 border border-white/10 rounded px-2 py-1 text-white" />
                <span className="text-neutral-500">a</span>
                <input data-testid="wa-delay-max" type="number" min={3} max={300} value={delays.max} onChange={(e) => setDelays((d) => ({ ...d, max: e.target.value }))} className="w-20 bg-black/40 border border-white/10 rounded px-2 py-1 text-white" />
                <span className="text-[10px] text-neutral-500">recomendado 5–12 s para proteger tu número</span>
              </div>
            </div>

            <div className="rounded-lg bg-black/40 border border-white/5 p-3">
              <div className="text-[10px] uppercase tracking-widest text-neutral-400 mb-1.5">Enviar mensaje de prueba</div>
              <div className="flex flex-col sm:flex-row gap-2">
                <input data-testid="wa-test-phone" value={testPhone} onChange={(e) => setTestPhone(e.target.value)} placeholder="+52 662 000 0000" className="sm:w-44 bg-black/40 border border-white/10 rounded px-2 py-1.5 text-white text-xs" />
                <input data-testid="wa-test-text" value={testText} onChange={(e) => setTestText(e.target.value)} className="flex-1 bg-black/40 border border-white/10 rounded px-2 py-1.5 text-white text-xs" />
                <button data-testid="wa-test-send" disabled={!st.connected || busy === "test" || !testPhone} onClick={sendTest} className="px-3 py-1.5 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black text-[10px] uppercase tracking-widest font-bold flex items-center justify-center gap-1 disabled:opacity-40">
                  {busy === "test" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Probar
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Templates */}
      <div className="glass-dark rounded-2xl p-5">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <div>
            <h3 className="font-serif text-lg text-white flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#d4af37]" /> Plantillas de mensajes</h3>
            <p className="text-[11px] text-neutral-400">Usa las variables entre llaves; se rellenan automáticamente. Puedes desactivar eventos individualmente.</p>
          </div>
          <div className="flex gap-2">
            <button data-testid="wa-generate-ai" disabled={busy === "ai"} onClick={generateAI} className="text-[10px] px-3 py-1.5 rounded-full border border-fuchsia-400/50 text-fuchsia-200 hover:bg-fuchsia-500/10 flex items-center gap-1 disabled:opacity-50">
              {busy === "ai" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />} Generar con IA
            </button>
            <button data-testid="wa-save-templates" disabled={busy === "save"} onClick={saveTemplates} className="text-[10px] px-3 py-1.5 rounded-full bg-[#d4af37] hover:bg-[#f3d878] text-black font-bold flex items-center gap-1 disabled:opacity-50">
              {busy === "save" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Guardar
            </button>
          </div>
        </div>
        <div className="space-y-2">
          {Object.entries(st.events).map(([ev, meta]) => {
            const open = openTpl === ev;
            return (
              <div key={ev} className="rounded-lg border border-white/10 bg-black/40" data-testid={`wa-template-${ev}`}>
                <div className="flex items-center gap-2 px-3 py-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input data-testid={`wa-event-toggle-${ev}`} type="checkbox" checked={eventsEnabled[ev] !== false} onChange={(e) => setEventsEnabled((s) => ({ ...s, [ev]: e.target.checked }))} className="accent-emerald-500" />
                    <span className="text-xs text-white font-semibold">{meta.label}</span>
                  </label>
                  <button type="button" onClick={() => setOpenTpl(open ? null : ev)} className="ml-auto text-[10px] text-[#d4af37] hover:underline">{open ? "Cerrar" : "Editar"}</button>
                </div>
                {open && (
                  <div className="px-3 pb-3">
                    <textarea data-testid={`wa-template-text-${ev}`} rows={5} value={templates[ev] || ""} onChange={(e) => setTemplates((t) => ({ ...t, [ev]: e.target.value }))} className="w-full bg-black/60 border border-white/10 rounded px-2 py-1.5 text-white text-xs leading-relaxed focus:outline-none focus:border-[#d4af37]" />
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {meta.vars.map((v) => <button key={v} type="button" onClick={() => setTemplates((t) => ({ ...t, [ev]: `${t[ev] || ""} {${v}}` }))} className="text-[9px] px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-neutral-300 hover:text-white">{`{${v}}`}</button>)}
                      <button type="button" onClick={() => setTemplates((t) => ({ ...t, [ev]: st.defaults[ev] }))} className="text-[9px] px-1.5 py-0.5 rounded border border-amber-400/40 text-amber-200 ml-auto">Restaurar</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Outbox */}
      <div className="glass-dark rounded-2xl p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-serif text-lg text-white flex items-center gap-2"><Send className="w-4 h-4 text-[#d4af37]" /> Bandeja de envíos</h3>
          <button onClick={loadOutbox} className="text-[10px] text-neutral-400 hover:text-white flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Actualizar</button>
        </div>
        {outbox.length === 0 ? <p className="text-xs text-neutral-500 italic">Aún no hay mensajes.</p> : (
          <div className="space-y-1.5 max-h-96 overflow-y-auto pr-1" data-testid="wa-outbox">
            {outbox.map((m) => {
              const [lbl, cls, Icon] = OUTBOX_STYLE[m.status] || OUTBOX_STYLE.queued;
              const wa = m.phone ? `https://wa.me/${m.phone}?text=${encodeURIComponent(m.text)}` : null;
              return (
                <div key={m.id} data-testid={`wa-outbox-${m.id}`} className="rounded-lg border border-white/5 bg-black/40 px-3 py-2 text-xs">
                  <div className="flex items-center gap-2">
                    <Icon className={`w-3.5 h-3.5 ${cls}`} />
                    <span className="text-white font-semibold truncate">{m.user_name}</span>
                    <span className="text-neutral-500 truncate">· {st.events[m.event]?.label || m.event}</span>
                    <span className={`ml-auto text-[10px] uppercase tracking-widest ${cls}`}>{lbl}</span>
                  </div>
                  <div className="text-[10px] text-neutral-500 mt-0.5">{fmtDT(m.created_at)} · {m.phone || "sin teléfono"}{m.error ? ` · ${m.error}` : ""}</div>
                  <details className="mt-1"><summary className="text-[10px] text-neutral-400 cursor-pointer">Ver mensaje</summary><pre className="whitespace-pre-wrap text-[11px] text-neutral-300 mt-1 font-sans">{m.text}</pre></details>
                  <div className="flex gap-1.5 mt-1.5">
                    {wa && m.status !== "sent" && <a href={wa} target="_blank" rel="noreferrer" onClick={() => outboxAction(m.id, "mark-sent")} className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-200 border border-emerald-400/40 flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Enviar manual</a>}
                    {(m.status === "failed" || m.status === "manual") && m.phone && <button onClick={() => outboxAction(m.id, "retry")} className="text-[10px] px-2 py-0.5 rounded-full border border-sky-400/40 text-sky-200">Reintentar</button>}
                    {m.status !== "sent" && <button onClick={() => outboxAction(m.id, "delete")} className="text-[10px] px-2 py-0.5 rounded-full border border-white/10 text-neutral-400 hover:text-rose-300 flex items-center gap-1"><Trash2 className="w-3 h-3" /></button>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default WhatsAppPanel;
